import { useCallback, useEffect, useRef, useState } from 'react';
import {
  requestCameraPermission,
  enumerateCameras,
  switchCamera,
  getActiveCameraStream,
  CAMERA_ERROR_CODE,
} from '../../../services/camera-manager';
import LoadingElement from '../../loading-element';
import CameraAccess from '../../../assets/svgs/Access=Allowed, Status=On, Interactivity=Default.svg';
import NotAllowsAccess from '../../../assets/svgs/Access=Not Allowed, Status=Off, Interactivity=Default.svg';
import AllowsAccessButUse from '../../../assets/svgs/Access=Allowed, Status=Off, Interactivity=Default.svg';
import CameraPermissionSelectList from '../camera-permission-select-list/camera-permission-select-list';
import CameraProblemSection from '../cameraa-problem-section/cameraa-problem-section';
import CameraUnavailablePanel from '../camera-unavailable-panel/camera-unavailable-panel';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

// Portrait mobile: this side owns a fixed 70% row of the viewport (see the grid
// in features/camera-permission), so the preview just takes whatever that row
// has left after the device pickers instead of carrying its own viewport-unit
// height. Landscape and desktop keep their percentage heights.
const previewStyle = tw`rounded-[14px] bg-[#E7E7E7] w-full max-lg:[@media(orientation:portrait)]:flex-1 max-lg:[@media(orientation:portrait)]:min-h-0 max-lg:[@media(orientation:landscape)]:h-[55%] lg:h-[70%] overflow-hidden flex [align-self: center] relative`;

const CameraSide = ({
  onContinueWithoutCamera,
  setPermissionsGranted,
  setIsCameraInUse,
  setNoCameraIsavalebale,
  setSelectedCamera,
  selectedCamera,
  setIsActive,
  isActive,
  permissionsGranted,
  permissionChecked,
  setPermissionChecked,
  cameraInUse,
  noCameraAvailable,
}) => {
  const [medaiDevices, setMediaDevices] = useState([]);
  const [selectedCameraDefaultLabel, setSelectedCameraDefault] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const cameraRef = useRef(null);
  const { t } = useTranslation('camera-permission');

  const attachStreamToPreview = stream => {
    if (cameraRef.current && cameraRef.current.srcObject !== stream) {
      cameraRef.current.srcObject = stream ?? null;
    }
  };

  // Only after permission is granted (labels are blank/generic before that on
  // iOS) — shapes CameraManager's richer camera objects into the `{label,value}`
  // pairs this page's generic <select> already expects.
  const refreshCameraList = async currentDeviceId => {
    const { cameras } = await enumerateCameras();
    const options = cameras.map(camera => ({ label: camera.label, value: camera.deviceId }));
    setMediaDevices(options);
    setSelectedCameraDefault(options.find(option => option.value === currentDeviceId) ?? null);
    if (currentDeviceId) {
      setSelectedCamera(currentDeviceId);
    }
  };

  // The one getUserMedia call this page makes: a generic, low-res permission
  // request (see services/camera/constraints.js) whose resulting stream is
  // kept and reused rather than thrown away — closing the old flow's 2-3
  // redundant preflight probes on every mount.
  //
  // Also the retry path: whatever was holding the camera can release it while
  // this page is still open, so the unavailable panel re-runs exactly this
  // instead of asking the user to reload. Both error flags are cleared first —
  // a retry that now succeeds, or fails differently, must not leave the
  // previous state's frame on screen.
  const initCamera = useCallback(
    async ({ isCancelled = () => false } = {}) => {
      setIsCameraInUse(false);
      setNoCameraIsavalebale(false);

      const { granted, stream, settings, error } = await requestCameraPermission();
      if (isCancelled()) {
        return;
      }

      localStorage.setItem('cameraPermissionGranted', String(granted));
      setPermissionChecked(true);
      setPermissionsGranted(granted);

      if (!granted) {
        if (error?.code === CAMERA_ERROR_CODE.NO_CAMERA) {
          setNoCameraIsavalebale(true);
        } else if (error?.code === CAMERA_ERROR_CODE.CAMERA_IN_USE) {
          setIsCameraInUse(true);
        }
        return;
      }

      attachStreamToPreview(stream);
      setIsActive(Boolean(stream?.active));
      await refreshCameraList(settings?.deviceId);
    },
    // The setters are stable (useState) and the two local helpers only touch
    // refs and state — see the mount-once effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    let cancelled = false;

    void initCamera({ isCancelled: () => cancelled });

    return () => {
      cancelled = true;
    };
    // Runs once on mount only — see the equivalent effects this replaced.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetryCamera = async () => {
    if (isRetrying) {
      return;
    }

    setIsRetrying(true);
    try {
      await initCamera();
    } finally {
      setIsRetrying(false);
    }
  };

  // A device picked from the dropdown below. Never a bare
  // `{ deviceId: { exact } }` request with no recovery: switchCamera tries
  // deviceId, falls back to facingMode, and — if both fail — leaves whatever
  // was already running untouched (see services/camera/store.js), so a failed
  // pick never blacks out the preview.
  const previousSelectedCameraRef = useRef(selectedCamera);
  useEffect(() => {
    if (
      !permissionsGranted ||
      !selectedCamera ||
      previousSelectedCameraRef.current === selectedCamera
    ) {
      previousSelectedCameraRef.current = selectedCamera;
      return undefined;
    }
    previousSelectedCameraRef.current = selectedCamera;

    let cancelled = false;

    (async () => {
      const { ok } = await switchCamera({ deviceId: selectedCamera });
      if (cancelled || !ok) {
        return;
      }
      attachStreamToPreview(getActiveCameraStream());
      setIsActive(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedCamera, permissionsGranted, setIsActive]);

  // The camera-in-use state renders the panel *instead of* the preview, so the
  // <video> is unmounted while it is on screen — which means the attach inside
  // initCamera lands on a null ref when a retry succeeds. Re-attach here, after
  // the element is back. Idempotent: attachStreamToPreview no-ops when the
  // element already holds this stream.
  useEffect(() => {
    if (!permissionsGranted || cameraInUse) {
      return;
    }

    attachStreamToPreview(getActiveCameraStream());
    setIsActive(Boolean(getActiveCameraStream()?.active));
  }, [permissionsGranted, cameraInUse, setIsActive]);

  useEffect(() => {
    return () => {
      // Detach the preview only — the underlying stream stays alive in
      // CameraManager (this is a client-side route change to /try-demo, never
      // a full reload) so /exercise can reuse it without a second getUserMedia
      // call, which is what made Safari/iOS re-prompt on every visit there.
      if (cameraRef.current) {
        cameraRef.current.srcObject = null;
      }

      setIsActive(false);
    };
  }, [setIsActive]);

  // Nothing in this state has a camera behind it: the preview frame would be an
  // empty grey box around the card, and the device picker an always-disabled
  // "No camera found". Both are dropped so the alert is the whole column.
  if (cameraInUse) {
    return (
      <div tw="flex justify-center items-center px-4 w-full h-full min-h-0">
        <CameraUnavailablePanel
          onRetry={handleRetryCamera}
          onContinueWithoutCamera={onContinueWithoutCamera}
          isRetrying={isRetrying}
        />
      </div>
    );
  }

  return (
    <div tw="w-full h-full min-h-0 relative flex flex-col justify-center px-4 lg:px-0">
      {!isActive && permissionsGranted && (
        <div tw="w-full z-10 h-full absolute inset-0 flex items-center justify-center">
          <div tw="lg:hidden ">
            <LoadingElement width={60} />
          </div>
          <div tw="hidden lg:block">
            <LoadingElement width={100} />
          </div>
        </div>
      )}
      <div css={previewStyle}>
        {permissionChecked && !permissionsGranted && !noCameraAvailable && (
          <CameraProblemSection
            text={t('cameraSide.cameraAccessPreferred')}
            icon={NotAllowsAccess}
            iconAlt={t('cameraSide.altCameraAccessBlocked')}
          />
        )}
        <video
          ref={cameraRef}
          autoPlay
          muted
          playsInline
          tw="h-full w-full object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />
        {!noCameraAvailable && permissionsGranted && (
          <img
            loading="lazy"
            decoding="async"
            src={CameraAccess}
            tw="absolute bottom-4 left-[50%] translate-x-[-50%] md:w-[60px] w-[48px]"
            alt={t('cameraSide.altCameraAccessed')}
          />
        )}
        {!noCameraAvailable && permissionChecked && !permissionsGranted && (
          <img
            loading="lazy"
            decoding="async"
            src={NotAllowsAccess}
            tw="hidden md:block absolute bottom-4 left-[50%] translate-x-[-50%] w-[60px]"
            alt={t('cameraSide.altCameraAccessBlocked')}
          />
        )}
        {noCameraAvailable && (
          <img
            loading="lazy"
            decoding="async"
            src={AllowsAccessButUse}
            tw="absolute bottom-4 left-[50%] translate-x-[-50%] w-[90px] lg:w-[150px] h-auto"
            alt={t('cameraSide.altCameraAccessed')}
          />
        )}
      </div>
      <CameraPermissionSelectList
        medaiDevices={medaiDevices}
        microphones={[]}
        selectedAudioDefaultLabel={null}
        selectedCameraDefaultLabel={selectedCameraDefaultLabel}
        setSelectedCamera={setSelectedCamera}
        setSelectedMicrophone={() => {}}
        disabled={!permissionsGranted}
      />
    </div>
  );
};

export default CameraSide;
